/**
 * Front / Middle / Back of green distance helpers.
 *
 * We don't have OSM green-polygon data per hole yet, so we approximate:
 *   - Middle = the pin coords (what the course gives us)
 *   - Front  = pin moved ~14 yds BACK along the player→pin bearing
 *              (i.e. 14 yds closer to the player)
 *   - Back   = pin moved ~14 yds FORWARD along the same bearing
 *
 * 14 yds × 2 = a 28-yd-deep green, which is the UK average for most parkland
 * courses. The figures are intentionally a touch conservative — better to
 * leave the ball pin-high than fly the back.
 *
 * Once we ingest OSM polygons we can swap this for real per-hole F/M/B.
 */
const M_PER_YD = 0.9144;
const HALF_GREEN_M = 14 * M_PER_YD;

function bearingRad(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLng = toRad(lng2 - lng1);
  const y = Math.sin(dLng) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLng);
  return Math.atan2(y, x);
}

function offsetLatLng(lat, lng, bearingRadians, meters) {
  const R = 6371000;
  const angDist = meters / R;
  const lat1 = (lat * Math.PI) / 180;
  const lng1 = (lng * Math.PI) / 180;
  const sinLat2 =
    Math.sin(lat1) * Math.cos(angDist) +
    Math.cos(lat1) * Math.sin(angDist) * Math.cos(bearingRadians);
  const lat2 = Math.asin(sinLat2);
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(bearingRadians) * Math.sin(angDist) * Math.cos(lat1),
      Math.cos(angDist) - Math.sin(lat1) * sinLat2
    );
  return { lat: (lat2 * 180) / Math.PI, lng: (lng2 * 180) / Math.PI };
}

function distYds(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const m = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(m / M_PER_YD);
}

/**
 * Compute approximate Front / Middle / Back distances in yards.
 * Returns { front, middle, back, frontPos, backPos } — positions are useful
 * for marker placement on a satellite map view.
 * Returns null if any required coords are missing.
 */
export function frontMiddleBack(playerLat, playerLng, pinLat, pinLng) {
  if (
    playerLat == null ||
    playerLng == null ||
    pinLat == null ||
    pinLng == null
  ) {
    return null;
  }
  const middle = distYds(playerLat, playerLng, pinLat, pinLng);
  const brng = bearingRad(playerLat, playerLng, pinLat, pinLng);
  // Front = closer to player (negative along bearing)
  const frontPos = offsetLatLng(pinLat, pinLng, brng + Math.PI, HALF_GREEN_M);
  // Back = further away (positive along bearing)
  const backPos = offsetLatLng(pinLat, pinLng, brng, HALF_GREEN_M);
  const front = distYds(playerLat, playerLng, frontPos.lat, frontPos.lng);
  const back = distYds(playerLat, playerLng, backPos.lat, backPos.lng);
  return { front, middle, back, frontPos, backPos };
}
